import { useEffect, useRef } from 'react';
import { captureException } from '@sentry/react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useCheckImpersonatorImpersonation } from '@/api/generated/endpoints/impersonation';
import type { CheckImpersonatorResponseSchema } from '@/api/generated/schemas';
import type { ErrorType } from '../mutators/IgniteAxiosClient';
import { IMPERSONATION } from '@/constants/queries';

interface ImpersonationErrorData {
  message?: string;
}

interface UseImpersonatorStatusQueryOptions {
  enabled?: boolean;
  queryOptions?: Partial<
    UseQueryOptions<CheckImpersonatorResponseSchema, ErrorType<ImpersonationErrorData>, CheckImpersonatorResponseSchema>
  >;
}

/**
 * Hook to check if the current authenticated user has impersonator privileges.
 *
 * This query checks the `/impersonation/impersonation/impersonator` endpoint which returns
 * whether the user is registered as an impersonator (has permission to access support features).
 *
 * Used for displaying the UserDialogMenu options for impersonating or giving support access.
 *
 * Note: This does NOT check who they're impersonating - it only checks if they have
 * impersonator privileges.
 *
 * @param options - Query configuration options
 * @param options.enabled - Whether the query should run (default: true)
 * @param options.queryOptions - Additional React Query options to pass through
 * @returns React Query result with `data.impersonator` boolean
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useImpersonatorStatusQuery();
 * const hasImpersonatorAccess = data?.impersonator ?? false;
 * ```
 */
export function useImpersonatorStatusQuery({ enabled = true, queryOptions }: UseImpersonatorStatusQueryOptions = {}) {
  const query = useCheckImpersonatorImpersonation({
    query: {
      enabled,
      staleTime: IMPERSONATION.STALE_TIME_MS,
      retry: false,
      ...queryOptions,
    },
  });

  const loggedErrorRef = useRef<ErrorType<ImpersonationErrorData> | null>(null);

  /**
   * Error logging side effect.
   * React Query v5 removed onError callbacks from query options.
   * This effect logs errors to Sentry for monitoring without blocking the UI.
   * Uses ref to prevent duplicate logs for the same error instance.
   */
  useEffect(() => {
    if (query.error && query.error !== loggedErrorRef.current) {
      loggedErrorRef.current = query.error;
      captureException(query.error, {
        level: 'warning',
        tags: {
          feature: 'impersonation',
          context: 'impersonator-status-check',
        },
        extra: {
          message: 'Failed to check impersonator status',
        },
      });
    }
  }, [query.error]);

  return query;
}
